// Rendering del corpo articolo a partire dai blocchi del CMS (BlogBlock[]).
// Mappa 1:1 i tipi di blocco al markup di design (.prose / .callout / .spec /
// .pullquote / .codeblock / .art-figure). Il testo prosa (html) viene reso così com'è dall'editor.
//
// processBody() fa una sola passata deterministica sui blocchi: inietta gli id sugli
// <h2> (slug dal testo) e costruisce il TOC dagli stessi heading. Gli id finiscono
// nell'HTML statico (SSG) → ancore e indice funzionano anche senza JS.
import { Icons } from '@/components/Icons'
import type { BlogBlock } from '@/data/blog'

export interface TocEntry {
  id: string
  label: string
}

function slugify(text: string): string {
  const s = text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return s || 'sezione'
}

function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

function uniqueId(base: string, used: Set<string>): string {
  let id = base
  let n = 2
  while (used.has(id)) id = `${base}-${n++}`
  used.add(id)
  return id
}

// Inietta gli id sugli <h2> e raccoglie le voci del TOC, preservando l'ordine dei
// blocchi. Ritorna i blocchi (con html aggiornato) + il TOC, da una sola sorgente.
export function processBody(blocks: BlogBlock[]): { blocks: BlogBlock[]; toc: TocEntry[] } {
  const used = new Set<string>()
  const toc: TocEntry[] = []

  const out = blocks.map((block): BlogBlock => {
    if (block.type !== 'html') return block
    const html = block.html.replace(
      /<h2(\b[^>]*)>([\s\S]*?)<\/h2>/gi,
      (match, attrs: string, inner: string) => {
        const label = stripTags(inner)
        const id = uniqueId(slugify(label), used)
        toc.push({ id, label })
        if (/\sid\s*=/.test(attrs)) return match // rispetta un id già presente
        return `<h2${attrs} id="${id}">${inner}</h2>`
      },
    )
    return { ...block, html }
  })

  return { blocks: out, toc }
}

function BlockView({ block }: { block: BlogBlock }) {
  switch (block.type) {
    case 'html':
      return <div dangerouslySetInnerHTML={{ __html: block.html }} />
    case 'callout':
      return (
        <div className="callout">
          <span className="ic">{block.icon === 'info' ? <Icons.info /> : <Icons.bolt />}</span>
          <div dangerouslySetInnerHTML={{ __html: block.html }} />
        </div>
      )
    case 'specTable':
      return (
        <div className="spec">
          <div className="spec-head"><span className="dots"><i /><i /><i /></span> {block.title}</div>
          <table className="spec-table">
            <tbody>
              {block.rows.map((r, i) => (
                <tr key={i}><td>{r.label}</td><td>{r.value}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'pullquote':
      return <p className="pullquote">{block.text}</p>
    case 'codeblock':
      return (
        <div className="codeblock">
          <div className="codeblock-head"><span className="dots"><i /><i /><i /></span> {block.title}</div>
          <pre><code>{block.code}</code></pre>
        </div>
      )
    case 'image':
      return (
        <figure className="art-figure">
          <img src={block.src} alt={block.alt} width={block.width} height={block.height} loading="lazy" decoding="async" />
        </figure>
      )
  }
}

export function BlogBody({ blocks }: { blocks: BlogBlock[] }) {
  return (
    <>
      {blocks.map((block, i) => <BlockView key={i} block={block} />)}
    </>
  )
}
